import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";

import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode } from "swiper/modules";

import ProductCardNew from "../../ProductCard/ProductCardNew.jsx";

import "swiper/css";
import "./HomepageSections.css";



export default function HomepageSection({ section }) {
  const categories = section?.categories ?? [];

  const [selectedCategoryId, setSelectedCategoryId] = useState(
    categories[0]?.id ?? null,
  );

  const categorySwiperRef = useRef(null);
  const productSwiperRef = useRef(null);



  /*
    |--------------------------------------------------------------------------
    | Keep selected category valid
    |--------------------------------------------------------------------------
    */

  useEffect(() => {
    if (!categories.length) {
      setSelectedCategoryId(null);
      return;
    }

    const exists = categories.some(
      (category) => category.id === selectedCategoryId,
    );

    if (!exists) {
      setSelectedCategoryId(categories[0].id);
    }
  }, [categories, selectedCategoryId]);

  /*
    |--------------------------------------------------------------------------
    | Selected category
    |--------------------------------------------------------------------------
    */

  const selectedCategory = categories.find(
    (category) => category.id === selectedCategoryId,
  );

  const products = selectedCategory?.products ?? [];

  /*
    |--------------------------------------------------------------------------
    | Category click
    |--------------------------------------------------------------------------
    */

  const handleCategoryChange = (category) => {
    setSelectedCategoryId(category.id);
  };

  /*
    |--------------------------------------------------------------------------
    | Update product swiper when category changes
    |--------------------------------------------------------------------------
    */

  useEffect(() => {
    const swiper = productSwiperRef.current;

    if (!swiper || swiper.destroyed) {
      return;
    }

    swiper.slideTo(0, 0);
  }, [selectedCategoryId]);

  /*
    |--------------------------------------------------------------------------
    | No categories
    |--------------------------------------------------------------------------
    */

  if (!categories.length) {
    return null;
  }



  const sectionBannersDesktop = {
    ammazing: {
      status: 1,
      image: "/images/banners/amazing.png",
      href: "/products?amazing=1",
      alt: "پیشنهادهای شگفت‌انگیز",
    },

    newest: {
      status: 0,
      image: "/images/banners/amazing.png",
      href: "#",
      alt: "جدیدترین محصولات",
    },

    most_visited: {
      status: 0,
      image: "/images/banners/amazing.png",
      href: "#",
      alt: "پربازدیدترین محصولات",
    },

    most_popular: {
      status: 0,
      image: "/images/banners/amazing.png",
      href: "#",
      alt: "پرفروش ترین محصولات",
    },
  };

  const sectionBanners = {
    ammazing: {
      status: 1,
      image: "/images/banners/amazing-mobile.png",
      href: "/products?amazing=1",
      alt: "پیشنهادهای شگفت‌انگیز",
    },

    newest: {
      status: 0,
      image: "/images/banners/amazing-mobile.png",
      href: "#",
      alt: "جدیدترین محصولات",
    },

    most_visited: {
      status: 0,
      image: "/images/banners/amazing-mobile.png",
      href: "#",
      alt: "پربازدیدترین محصولات",
    },

    most_popular: {
      status: 0,
      image: "/images/banners/amazing-mobile.png",
      href: "#",
      alt: "پرفروش ترین محصولات",
    },
  };

  const bannerImageDesk = sectionBannersDesktop[section.type];
  const bannerImage = sectionBanners[section.type];

  return (


    <section className={`homepage-section homepage-section-${section.type}`}>
      <div className="homepage-section-layout">

        {/* MAIN CONTENT */}
        <div className="homepage-section-content">


          {/* MOBILE AND TABLET BANNER */}
          {bannerImage?.status === 1 && (
            <Link
              to={bannerImage.href}
              className="homepage-section-mobile-banner"
            >
              <img
                src={bannerImage.image}
                alt={`${section.type} banner`}
                width="100%"
                height="58"
              />
            </Link>
          )}


          {/* HEADER */}
          <div className="homepage-section-header">
            <div className="homepage-section-title-wrapper">
              <span className="homepage-section-title-icon">✦</span>

              <div className="homepage-section-title-content">
                <h2 className="homepage-section-title">{section.name}</h2>

                {section.subtitle && (
                  <span className="homepage-section-subtitle">
                    {section.subtitle}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* CATEGORY TABS */}
          <div className="homepage-category-tabs-wrapper">
            <Swiper
              onSwiper={(swiper) => {
                categorySwiperRef.current = swiper;
              }}
              modules={[FreeMode]}
              direction="horizontal"
              dir="rtl"
              freeMode={{ enabled: true, sticky: false }}
              slidesPerView="auto"
              spaceBetween={8}
              watchOverflow={false}
              resistance
              resistanceRatio={0.7}
              className="homepage-category-tabs"
            >
              {categories.map((category) => {
                const isActive = category.id === selectedCategoryId;

                return (
                  <SwiperSlide
                    key={category.id}
                    className="homepage-category-tab-slide"
                  >
                    <button
                      type="button"
                      className={`homepage-category-tab ${isActive ? "active" : ""
                        }`}
                      onClick={() => handleCategoryChange(category)}
                    >
                      {category.name}
                    </button>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>

          {/* PRODUCTS */}
          <div className="homepage-products-wrapper">
            {products.length > 0 ? (
              <Swiper
                onSwiper={(swiper) => {
                  productSwiperRef.current = swiper;
                }}
                modules={[FreeMode]}
                direction="horizontal"
                dir="rtl"
                freeMode={{ enabled: true, sticky: false }}
                slidesPerView="auto"
                spaceBetween={8}
                watchOverflow
                resistance
                resistanceRatio={0.7}
                className="homepage-products"
              >
                {products.map((product) => (
                  <SwiperSlide
                    key={product.id}
                    className="homepage-product-slide"
                  >
                    <ProductCardNew product={product} />
                  </SwiperSlide>
                ))}
              </Swiper>
            ) : (
              <div className="homepage-empty">
                محصولی در این دسته وجود ندارد.
              </div>
            )}
          </div>

        </div>

        {/* RIGHT BANNER */}


        {bannerImageDesk?.status === 1 && (
          <Link
            to={bannerImageDesk.href}
            className="homepage-section-banner"
          >
            <img
              src={bannerImageDesk.image}
              alt={`${section.type} banner`}
              width={260}
              height={418}

            />
          </Link>
        )}


      </div>
    </section>



  );
}
